import Drawer, {DrawerRef} from 'devextreme-react/drawer';
import ScrollView, {ScrollViewRef} from 'devextreme-react/scroll-view';
import React, {useState, useCallback, useRef, useMemo, useEffect} from 'react';
import {useNavigate} from 'react-router';
import {Header, SideNavigationMenu, Footer} from '../../components';
import {navigation} from '../../app-navigation';
import './side-nav-outer-toolbar.scss';
import {useScreenSize} from '../../utils/media-query';
import {Template} from 'devextreme-react/core/template';
import {useMenuPatch} from '../../utils/patches';
import {ClickEvent} from 'devextreme/ui/button';
import {ItemClickEvent} from 'devextreme/ui/tree_view';
import type {SideNavToolbarProps} from '../../types';

export default function SideNavOuterToolbar({title, children}: React.PropsWithChildren<SideNavToolbarProps>) {
    const scrollViewRef = useRef<ScrollViewRef>(null);
    const navigate = useNavigate();
    const {isXSmall, isLarge} = useScreenSize();
    const [patchCssClass, onMenuReady] = useMenuPatch();
    const [renderedPath, setRenderedPath] = useState('');
    const [menuStatus, setMenuStatus] = useState(
        !isXSmall ? MenuStatus.Opened : MenuStatus.Closed
    );
    const drawerRef = useRef<DrawerRef>(null);

    function normalizePath() {
        return navigation.map((item) => (
            {...item, expanded: isLarge, path: item.path}
        ))
    }

    const toggleMenu = useCallback(({event}: ClickEvent) => {
        setMenuStatus(
            prevMenuStatus => prevMenuStatus === MenuStatus.Closed
                ? MenuStatus.Opened
                : MenuStatus.Closed
        );
        event?.stopPropagation();
    }, []);

    const temporaryOpenMenu = useCallback(() => {
        setMenuStatus(
            prevMenuStatus => prevMenuStatus === MenuStatus.Closed
                ? MenuStatus.TemporaryOpened
                : prevMenuStatus
        );
    }, []);

    const onOutsideClick = useCallback(() => {
        setMenuStatus(
            prevMenuStatus => {
                return prevMenuStatus !== MenuStatus.Closed
                    ? MenuStatus.Closed
                    : prevMenuStatus
            }
        );
        return true;
    }, []);

    const onNavigationChanged = useCallback(({itemData, event, node}: ItemClickEvent) => {
        if (menuStatus === MenuStatus.Closed || !itemData?.path || node?.selected) {
            event?.preventDefault();
            return;
        }

        navigate(itemData.path);
        scrollViewRef.current?.instance().scrollTo(0);

        if (!isLarge || menuStatus === MenuStatus.TemporaryOpened) {
            setMenuStatus(MenuStatus.Closed);
            event?.stopPropagation();
        }
    }, [navigate, menuStatus, isLarge]);


    // const renderedPath = useMemo(() => {
    //   if (navigation) {
    //     let result = ''
    //     function concatSelectedText(menuItems: any) {
    //       for (const menuItem of menuItems) {
    //         if (menuItem.selected) {
    //           if (result == '')
    //             result = result.concat(menuItem.text);
    //           else
    //             result = result.concat(menuItem.text + " → " + result);
    //         }
    //         if (menuItem.items) {
    //           console.log(menuItem)
    //           concatSelectedText(menuItem.items);
    //           // if (result !== null) {
    //           //   return menuItem.text + " → " + result;
    //           // }
    //         }
    //       }
    //       return;
    //     }
    //     concatSelectedText(navigation);
    //     // console.log(selectedText)
    //     // if (selectedText) selectedText = selectedText.trim()
    //     console.log(result)
    //     return result
    //   }
    // }, [navigation]);

    function findSelectedChild(menu: any): string | null {
        function findSelectedRecursive(items: any, path: string[] = []): string | null {
            for (const item of items) {
                if (item.selected) {
                    return [...path, item.text].join(' → ');
                }
                if (item.items) {
                    const result = findSelectedRecursive(item.items, [...path, item.text]);
                    if (result) {
                        return result;
                    }
                }
            }
            return null;
        }

        for (const item of menu) {
            if (item.selected)
                return item.text
            const result = findSelectedRecursive(item.items || [], [item.text]);
            if (result) {
                return result;
            }
        }

        return null;
    }

    useEffect(() => {
        const items = normalizePath();
        const timeoutId = setTimeout(() => {
            if (items) {
                const selectedPath = findSelectedChild(items);
                if (selectedPath) {
                    setRenderedPath(selectedPath);
                } else {
                   // console.log("No item with 'selected' value set to true found.");
                }

            }
        }, 1000);
        return () => {
            clearTimeout(timeoutId);
        };
    }, [navigation, navigate]);

    useEffect(() => {
        if (drawerRef?.current) {
            const ele = drawerRef.current.instance().element();
            const func = (event: any) => {
                setMenuStatus(
                    prevMenuStatus => {
                        return prevMenuStatus !== MenuStatus.Closed
                            ? MenuStatus.Closed
                            : prevMenuStatus
                    }
                );
            };
            ele.addEventListener("mouseout", func);
            return () => ele.removeEventListener("mouseout", func);
        }
        return;
    }, [drawerRef]);

    return (
        <div className={'side-nav-outer-toolbar'}>
            <Header
                menuToggleEnabled
                toggleMenu={toggleMenu}
                renderedPath={renderedPath}
                title={title}
            />
            <Drawer
                ref={drawerRef}
                className={['drawer', patchCssClass].join(' ')}
                position={'before'}
                closeOnOutsideClick={onOutsideClick}
                openedStateMode={'overlap'}
                // openedStateMode={isLarge ? 'push' : 'overlap'}
                revealMode={isXSmall ? 'slide' : 'expand'}
                minSize={isXSmall ? 0 : 80}
                maxSize={320}
                shading={!isLarge}
                opened={menuStatus !== MenuStatus.Closed}
                template={'menu'}
                activeStateEnabled={true}
                hoverStateEnabled={true}
            >
                <div className={'container'}>
                        <div className={'content'}>
                            {React.Children.map(children, (item: any) => {
                                return item.type !== Footer && item;
                            })}
                        </div>
                        {React.Children.count(children) > 0 ? <div className={'content-block'}>
                            {React.Children.map(children, (item: any) => {
                                return item.type === Footer && item;
                            })}
                        </div> : null}
                </div>
                <Template name={'menu'}>
                    <SideNavigationMenu
                        compactMode={menuStatus === MenuStatus.Closed}
                        selectedItemChanged={onNavigationChanged}
                        openMenu={temporaryOpenMenu}
                        onMenuReady={onMenuReady}
                    >
                    </SideNavigationMenu>
                </Template>
            </Drawer>
        </div>
    );
}

const MenuStatus = {
    Closed: 1,
    Opened: 2,
    TemporaryOpened: 3
};
